import type { Filter, FiltersGroup, FiltersGroups, FilterSelection } from '@/app/common/filters/core/filtersTypes.ts';
import { applyUpdatedFilters } from '@/app/common/filters/core/filterUtils.ts';
import { isOptionSelected } from '@/app/common/filters/core/filterOptionSelection.ts';
import { chipBehaviourOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';
import type { FilterSessionValue } from '@/app/common/filters/session/sessionState.ts';
import { sameSessionValue } from '@/app/common/filters/session/sessionState.ts';
import { optionsOf } from '@/app/common/filters/session/selectedValues.ts';

const groupsTouchedBy = (filtersGroups: FiltersGroups, updatedFilters: Filter[]): FiltersGroups => {
  const updatedNames = new Set(updatedFilters.map((filter) => filter.name));

  return filtersGroups.filter(
    (group) => 'filters' in group && (group as FiltersGroup).filters.some((filter) => updatedNames.has(filter.name))
  );
};

const selectionsOfGroup = (group: FiltersGroup): FilterSelection[] =>
  group.filters.flatMap((filter) =>
    optionsOf(filter).map((option) => ({
      groupId: group.id as string,
      value: option.value,
      selected: isOptionSelected(option),
    }))
  );

const chipValuesIn = (groupId: string, filters: Filter[]): FilterSessionValue[] =>
  filters.flatMap((filter) =>
    (chipBehaviourOf(filter)?.options(filter) ?? [])
      .filter(isOptionSelected)
      .map((option) => ({ groupId, value: option.value }))
  );

const chipValuesOf = (filtersGroups: FiltersGroups): FilterSessionValue[] =>
  filtersGroups.flatMap((group) =>
    'filters' in group ? chipValuesIn(group.id as string, (group as FiltersGroup).filters) : []
  );

const addedTo = (was: FilterSessionValue[], now: FilterSessionValue[]): FilterSessionValue[] =>
  now.filter((value) => !was.some((one) => sameSessionValue(one, value)));

export const newlySelectedFrom = (before: FiltersGroups, after: FiltersGroups): FilterSessionValue[] =>
  addedTo(chipValuesOf(before), chipValuesOf(after));

export const newlySelectedInGroup = (groupId: string, before: Filter[], after: Filter[]): FilterSessionValue[] =>
  addedTo(chipValuesIn(groupId, before), chipValuesIn(groupId, after));

export type FilterSessionInput = {
  selections: FilterSelection[];
  justUsed: FilterSessionValue[];
  pressed: FilterSessionValue[];
};

export const sessionInputFrom = (
  filtersGroups: FiltersGroups,
  updatedFilters: Filter[],
  pressedOrder: FilterSessionValue[] = []
): FilterSessionInput => {
  const touched = groupsTouchedBy(filtersGroups, updatedFilters);
  const updated = applyUpdatedFilters({ prevFiltersGroups: touched, updatedFilters });
  const newly = newlySelectedFrom(touched, updated);
  const ranked = pressedOrder.filter((value) => newly.some((one) => sameSessionValue(one, value)));

  return {
    selections: updated.flatMap((group) => selectionsOfGroup(group as FiltersGroup)),
    justUsed: [...ranked, ...newly.filter((value) => !ranked.some((one) => sameSessionValue(one, value)))],
    pressed: [...ranked].reverse(),
  };
};
